import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Zap, CheckCircle, AlertCircle, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface Integration {
  id: string;
  name: string;
  description: string;
  category: 'nutrition' | 'health' | 'hardware' | 'fitness';
  status: 'available' | 'connected' | 'disabled';
  icon: string;
  provider: string;
}

interface ConnectionStatus {
  connected: boolean;
  lastSync?: string;
  connectedAt?: string;
}

export const ClientIntegrations: React.FC = () => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<Record<string, ConnectionStatus>>({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [platformConfigured, setPlatformConfigured] = useState(false);

  const categories = [
    { id: 'all', name: 'All', icon: '🔗' },
    { id: 'nutrition', name: 'Nutrition', icon: '🥗' },
    { id: 'health', name: 'Health', icon: '💊' },
    { id: 'hardware', name: 'Hardware', icon: '⌚' },
    { id: 'fitness', name: 'Fitness', icon: '🏃' }
  ];

  const availableIntegrations: Integration[] = [
    // Nutrition
    {
      id: 'fatsecret',
      name: 'FatSecret',
      description: 'Comprehensive nutrition database and food diary tracking',
      category: 'nutrition',
      status: 'available',
      icon: '🍎',
      provider: 'FatSecret Platform'
    },
    {
      id: 'myfitnesspal',
      name: 'MyFitnessPal',
      description: 'Popular calorie counter and nutrition tracker',
      category: 'nutrition',
      status: 'disabled',
      icon: '📱',
      provider: 'Under Armour'
    },
    {
      id: 'cronometer',
      name: 'Cronometer',
      description: 'Detailed micronutrient and macro tracking',
      category: 'nutrition',
      status: 'disabled',
      icon: '📊',
      provider: 'Cronometer'
    },

    // Health Platforms
    {
      id: 'google_fit',
      name: 'Google Fit',
      description: 'Activity and health data from Google ecosystem',
      category: 'health',
      status: 'disabled',
      icon: '🎯',
      provider: 'Google'
    },
    {
      id: 'apple_health',
      name: 'Apple Health',
      description: 'Comprehensive health data from iOS devices',
      category: 'health',
      status: 'disabled',
      icon: '🍎',
      provider: 'Apple'
    },

    // Smart Hardware
    {
      id: 'fitbit',
      name: 'Fitbit',
      description: 'Activity tracking, sleep monitoring, heart rate data',
      category: 'hardware',
      status: 'disabled',
      icon: '⌚',
      provider: 'Google'
    },
    {
      id: 'garmin',
      name: 'Garmin',
      description: 'Advanced fitness tracking and GPS data',
      category: 'hardware',
      status: 'disabled',
      icon: '🏃',
      provider: 'Garmin'
    },
    {
      id: 'oura',
      name: 'Oura Ring',
      description: 'Sleep, recovery, and readiness insights',
      category: 'hardware',
      status: 'disabled',
      icon: '💍',
      provider: 'Oura'
    },

    // Fitness
    {
      id: 'strava',
      name: 'Strava',
      description: 'Social fitness tracking and performance analysis',
      category: 'fitness',
      status: 'disabled',
      icon: '🏔️',
      provider: 'Strava'
    }
  ];

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Check platform configuration and load integration status
  useEffect(() => {
    const initialize = async () => {
      try {
        // Check if platform is configured
        const platformResponse = await fetch('/api/fatsecret/platform/status', {
          headers: getAuthHeaders()
        });
        const platformData = await platformResponse.json();
        setPlatformConfigured(platformData.configured === true);

        // Load user integration status
        if (user?.id) {
          await loadIntegrationStatus();
        }
      } catch (error) {
        console.error('Failed to initialize integrations:', error);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [user?.id]);

  // Listen for OAuth popup messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'FATSECRET_SUCCESS') {
        toast.success('Successfully connected to FatSecret!');
        setConnecting(null);
        loadIntegrationStatus();
      } else if (event.data.type === 'FATSECRET_ERROR') {
        toast.error(`Connection failed: ${event.data.error}`);
        setConnecting(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const loadIntegrationStatus = async () => {
    try {
      setIntegrations(availableIntegrations);

      if (!user?.id) {
        return;
      }

      const response = await fetch(`/api/fatsecret/user/status`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success) {
        const status: Record<string, ConnectionStatus> = {};
        if (data.connected) {
          status['fatsecret'] = {
            connected: true,
            connectedAt: data.connectedAt,
            lastSync: data.lastSync
          };
        }
        setConnectionStatus(status);

        // Update integration statuses
        const updatedIntegrations = availableIntegrations.map(integration => ({
          ...integration,
          status: status[integration.id]?.connected ? 'connected' as const : integration.status
        }));
        setIntegrations(updatedIntegrations);
      }
    } catch (error) {
      console.error('Failed to load integration status:', error);
    }
  };

  const handleConnect = async (integration: Integration) => {
    if (integration.status === 'disabled') {
      toast(`${integration.name} integration is coming soon!`);
      return;
    }

    if (!platformConfigured && integration.id === 'fatsecret') {
      toast.error('FatSecret is not configured. Please contact your administrator.');
      return;
    }

    if (integration.id === 'fatsecret') {
      try {
        setConnecting('fatsecret');

        const response = await fetch('/api/fatsecret/auth/start', {
          method: 'POST',
          headers: getAuthHeaders()
        });

        const data = await response.json();

        if (data.success && data.authUrl) {
          // Calculate centered popup position
          const popupWidth = 800;
          const popupHeight = 600;
          const left = window.screenX + (window.outerWidth - popupWidth) / 2;
          const top = window.screenY + (window.outerHeight - popupHeight) / 2;

          // Open OAuth popup
          const authWindow = window.open(
            data.authUrl,
            'fatsecret_auth',
            `width=${popupWidth},height=${popupHeight},left=${Math.round(left)},top=${Math.round(top)},scrollbars=yes,resizable=yes`
          );

          // Monitor popup for close
          const checkWindow = setInterval(() => {
            if (authWindow?.closed) {
              clearInterval(checkWindow);
              setConnecting(null);
              // Refresh status in case auth completed
              setTimeout(() => loadIntegrationStatus(), 1000);
            }
          }, 1000);
        } else {
          toast.error(data.message || 'Failed to start FatSecret authorization');
          setConnecting(null);
        }
      } catch (error) {
        console.error('FatSecret connection error:', error);
        toast.error('Failed to connect to FatSecret. Please try again.');
        setConnecting(null);
      }
    } else {
      toast(`${integration.name} integration is not yet implemented.`);
    }
  };

  const handleDisconnect = async (integration: Integration) => {
    if (!confirm(`Are you sure you want to disconnect from ${integration.name}?`)) {
      return;
    }

    try {
      const response = await fetch('/api/fatsecret/user/disconnect', {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Successfully disconnected from ${integration.name}`);
        await loadIntegrationStatus();
      } else {
        toast.error(`Failed to disconnect: ${data.message}`);
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Failed to disconnect. Please try again.');
    }
  };

  const filteredIntegrations = selectedCategory === 'all'
    ? integrations
    : integrations.filter(integration => integration.category === selectedCategory);

  const getStatusIcon = (status: string, connected?: boolean) => {
    if (connected) return <CheckCircle className="status-icon connected" />;
    if (status === 'disabled') return <AlertCircle className="status-icon disabled" />;
    return <Zap className="status-icon available" />;
  };

  if (loading) {
    return (
      <div className="integrations-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading integrations...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="integrations-container">
      {/* Header */}
      <div className="integrations-header">
        <h1>Connected Apps</h1>
        <p>Connect your favorite health, fitness, and nutrition apps to Mission Control</p>
      </div>

      {/* Category Filter */}
      <div className="category-filters">
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`category-chip ${selectedCategory === category.id ? 'active' : ''}`}
          >
            <span className="category-icon">{category.icon}</span>
            {category.name}
          </button>
        ))}
      </div>

      {/* Integration Grid */}
      <div className="integrations-grid">
        {filteredIntegrations.map(integration => {
          const connected = connectionStatus[integration.id]?.connected;
          const lastSync = connectionStatus[integration.id]?.lastSync;
          const isConnecting = connecting === integration.id;

          return (
            <div
              key={integration.id}
              className={`integration-card ${connected ? 'connected' : ''} ${integration.status === 'disabled' ? 'disabled' : ''}`}
            >
              {/* Status Indicator */}
              <div className="status-indicator">
                {getStatusIcon(integration.status, connected)}
              </div>

              {/* Card Content */}
              <div className="card-icon">
                <span>{integration.icon}</span>
              </div>

              <h3 className="card-title">{integration.name}</h3>
              <p className="card-provider">{integration.provider}</p>
              <p className="card-description">{integration.description}</p>

              {/* Last Sync */}
              {connected && lastSync && (
                <p className="last-sync">
                  Last sync: {new Date(lastSync).toLocaleDateString()}
                </p>
              )}

              {/* Action Button */}
              <div className="card-actions">
                {connected ? (
                  <button
                    onClick={() => handleDisconnect(integration)}
                    className="btn-disconnect"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(integration)}
                    disabled={integration.status === 'disabled' || isConnecting}
                    className="btn-connect"
                  >
                    {isConnecting ? (
                      <>
                        <span className="btn-spinner"></span>
                        Connecting...
                      </>
                    ) : integration.status === 'disabled' ? (
                      'Coming Soon'
                    ) : (
                      'Connect'
                    )}
                  </button>
                )}
              </div>

              {/* Settings icon for connected */}
              {connected && (
                <button className="settings-btn" title="Integration Settings">
                  <Settings size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredIntegrations.length === 0 && (
        <div className="empty-state">
          <Zap size={48} />
          <h3>No integrations found</h3>
          <p>Try selecting a different category.</p>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .integrations-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0;
  }

  .integrations-header {
    margin-bottom: 32px;
  }

  .integrations-header h1 {
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  .integrations-header p {
    font-size: 16px;
    color: var(--text-secondary);
    margin: 0;
  }

  /* Category Filters */
  .category-filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }

  .category-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: 24px;
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .category-chip:hover:not(.active) {
    background: var(--bg-secondary);
    border-color: var(--border-primary);
  }

  .category-chip.active {
    background: linear-gradient(135deg, var(--accent-blue), #8B5CF6);
    color: white;
    border-color: transparent;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  .category-icon {
    font-size: 16px;
  }

  /* Integration Grid */
  .integrations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }

  .integration-card {
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: 16px;
    padding: 24px;
    position: relative;
    transition: all 0.2s ease;
  }

  .integration-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .integration-card.connected {
    border: 2px solid var(--accent-green, #10B981);
    box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.1);
  }

  .integration-card.disabled {
    opacity: 0.7;
  }

  .integration-card.disabled:hover {
    transform: none;
  }

  /* Status Indicator */
  .status-indicator {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  .status-icon {
    width: 20px;
    height: 20px;
  }

  .status-icon.connected {
    color: var(--accent-green, #10B981);
  }

  .status-icon.available {
    color: var(--accent-blue);
  }

  .status-icon.disabled {
    color: var(--text-tertiary);
  }

  /* Card Content */
  .card-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--accent-blue), #8B5CF6);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-bottom: 16px;
  }

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 4px;
  }

  .card-provider {
    font-size: 13px;
    color: var(--text-tertiary);
    margin: 0 0 12px;
  }

  .card-description {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.5;
    margin: 0 0 16px;
    min-height: 42px;
  }

  .last-sync {
    font-size: 12px;
    color: var(--text-tertiary);
    margin: 0 0 16px;
  }

  /* Action Buttons */
  .card-actions {
    margin-top: auto;
  }

  .btn-connect {
    width: 100%;
    padding: 12px 20px;
    background: linear-gradient(135deg, var(--accent-blue), #8B5CF6);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .btn-connect:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  .btn-connect:disabled {
    background: var(--bg-secondary);
    color: var(--text-tertiary);
    cursor: not-allowed;
  }

  .btn-disconnect {
    width: 100%;
    padding: 12px 20px;
    background: transparent;
    color: var(--accent-red, #EF4444);
    border: 1px solid var(--accent-red, #EF4444);
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-disconnect:hover {
    background: var(--accent-red, #EF4444);
    color: white;
  }

  .btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  /* Settings Button */
  .settings-btn {
    position: absolute;
    bottom: 16px;
    right: 16px;
    background: transparent;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .settings-btn:hover {
    color: var(--text-secondary);
    background: var(--bg-secondary);
  }

  /* Loading State */
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    color: var(--text-secondary);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-blue);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--text-secondary);
  }

  .empty-state svg {
    color: var(--text-tertiary);
    margin-bottom: 16px;
  }

  .empty-state h3 {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  .empty-state p {
    margin: 0;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .integrations-header h1 {
      font-size: 24px;
    }

    .category-filters {
      flex-wrap: nowrap;
      overflow-x: auto;
      padding-bottom: 8px;
      -webkit-overflow-scrolling: touch;
    }

    .category-chip {
      flex-shrink: 0;
    }

    .integrations-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default ClientIntegrations;
